/**
 * DiscoveryPrompt — contextual suggestion card on the active dashboard
 *
 * Shows a single discovery prompt from the pulse endpoint. Manages dismissal
 * via localStorage (7-day cooldown, 2-strike permanent per prompt type).
 * Max 1 prompt visible at a time (enforced by backend returning single prompt).
 */

import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ArrowRight, MessageCircle, Users, BarChart3, Lightbulb, Share2, ImagePlus } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type { DiscoveryPrompt as DiscoveryPromptType } from "@/hooks/useDashboardPulse";
import { useDiscoveryDismissal } from "../hooks/useDiscoveryDismissal";
import { usePageTranslation } from "@/i18n/useTranslation";
import { dashboardTranslations } from "@/i18n/locales/en/dashboard";
import { dashboardTranslations as thDashboardTranslations } from "@/i18n/locales/th/dashboard";

const ICON_MAP: Record<string, LucideIcon> = {
  chatwoot_from_customers: MessageCircle,
  crm_from_customers: Users,
  insights_from_orders: BarChart3,
  storefront_share: Share2,
  products_add_images: ImagePlus,
};

function getPromptIcon(promptId: string): LucideIcon {
  return ICON_MAP[promptId] ?? Lightbulb;
}

interface DiscoveryPromptProps {
  discovery: DiscoveryPromptType | null;
}

export function DiscoveryPrompt({ discovery }: DiscoveryPromptProps) {
  const t = usePageTranslation({ en: dashboardTranslations, th: thDashboardTranslations });
  const { isDismissed, dismiss } = useDiscoveryDismissal(discovery?.id ?? null);
  const [isExiting, setIsExiting] = useState(false);
  const reducedMotion = useReducedMotion();

  if (!discovery || isDismissed || isExiting) return null;

  const Icon = getPromptIcon(discovery.id);

  const handleDismiss = () => {
    dismiss();
    setIsExiting(true);
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={reducedMotion ? false : { opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={reducedMotion ? { opacity: 0 } : { opacity: 0, y: -8 }}
        transition={{ duration: reducedMotion ? 0 : 0.2, delay: reducedMotion ? 0 : 0.03 }}
      >
        <div className="flex items-start gap-3 p-4 rounded-md bg-card border border-border">
          <div className="flex-shrink-0 w-9 h-9 rounded-sm bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center">
            <Icon className="w-4.5 h-4.5 text-blue-600 dark:text-blue-400" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-foreground">
              {discovery.message}
            </p>
            <Link
              to={discovery.href}
              className="inline-flex items-center gap-1 mt-2 text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
            >
              {discovery.cta}
              <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          <button
            onClick={handleDismiss}
            className="flex-shrink-0 text-xs text-muted-foreground hover:text-foreground transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded-md px-1 py-0.5"
            aria-label={t.discoveryDismissLabel}
          >
            {t.discoveryNotNow}
          </button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
